import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { MultiSelect } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { CompanyService } from '@/app/services/company.service';
import { CountryService } from '@/app/services/country.service';
import { RealmService } from '@/app/services/realm.service';
import { Company } from '@/lib-backend';
import { CompanyEditDialog } from './company-edit-dialog/company-edit-dialog';
import { CompanyDeleteDialog } from './company-delete-dialog/company-delete-dialog';

const HIGHLIGHT_DURATION_MS = 3000;

@Component({
    selector: 'app-companies',
    standalone: true,
    imports: [
        DatePipe, FormsModule, Button, IconField, InputIcon, InputText,
        Message, MultiSelect, TableModule, Tooltip, CompanyEditDialog, CompanyDeleteDialog,
    ],
    templateUrl: './companies.html',
    styleUrl: './companies.scss',
})
export class Companies implements OnInit {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly companyService = inject(CompanyService);
    protected readonly countryService = inject(CountryService);
    protected readonly realmService = inject(RealmService);

    protected readonly searchTerm = signal('');
    protected readonly selectedCountries = signal<string[]>([]);

    protected readonly editDialogVisible = signal(false);
    protected readonly editingCompany = signal<Company | null>(null);

    protected readonly deleteDialogVisible = signal(false);
    protected readonly deleteTarget = signal<Company | null>(null);

    /** ID of the most recently created company; cleared on a timer. */
    protected readonly highlightedId = signal<string | null>(null);
    private highlightTimer: ReturnType<typeof setTimeout> | null = null;

    /** Set from `?edit=<id>` query param. Effect opens the edit dialog as soon as companies finish loading. */
    private readonly pendingEditId = signal<string | null>(null);

    /** Hydrated from queryParams once on init; flips true to start syncing back to URL. */
    private urlHydrated = false;

    protected readonly websiteCountByCompany = computed(() => {
        const counts = new Map<string, number>();
        for (const r of this.realmService.realms()) {
            counts.set(r.CompanyId, (counts.get(r.CompanyId) ?? 0) + 1);
        }
        return counts;
    });

    protected readonly countryFilterOptions = computed(() => {
        // Only show countries actually used by loaded companies, sorted by name.
        const used = new Set<string>();
        for (const c of this.companyService.companies()) used.add(c.CountryCode);
        return [...used]
            .map(code => ({ code, name: this.countryService.getName(code) }))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(c => ({ label: `${c.name} (${c.code})`, value: c.code }));
    });

    protected readonly filteredCompanies = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        const countries = this.selectedCountries();
        const list = [...this.companyService.companies()].sort(
            (a, b) => b.CreatedAt.localeCompare(a.CreatedAt)
        );
        return list.filter(c => {
            if (countries.length > 0 && !countries.includes(c.CountryCode)) return false;
            if (!term) return true;
            return c.Name.toLowerCase().includes(term)
                || c.Address.toLowerCase().includes(term)
                || c.RegistrationDetails.toLowerCase().includes(term)
                || this.countryService.getName(c.CountryCode).toLowerCase().includes(term);
        });
    });

    protected readonly hasActiveFilters = computed(() =>
        this.searchTerm().trim().length > 0 || this.selectedCountries().length > 0
    );

    constructor() {
        // Sync filters → URL whenever they change (after initial hydration).
        effect(() => {
            const q = this.searchTerm().trim();
            const countries = this.selectedCountries();
            if (!this.urlHydrated) return;

            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {
                    q: q || null,
                    country: countries.length > 0 ? countries.join(',') : null,
                },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        });

        // Resolve a pending ?edit=<id> once companies have loaded. Re-runs when
        // either the requested id or the loaded company set changes.
        effect(() => {
            const id = this.pendingEditId();
            if (!id) return;
            if (this.companyService.loading()) return;

            const target = this.companyService.byId().get(id);
            if (target) this.openEdit(target);
            this.pendingEditId.set(null);
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { edit: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        });
    }

    ngOnInit(): void {
        this.companyService.loadCompanies();
        this.realmService.loadRealms();

        this.route.queryParamMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(params => {
                if (!this.urlHydrated) {
                    this.searchTerm.set(params.get('q') ?? '');
                    const country = params.get('country');
                    this.selectedCountries.set(country ? country.split(',').filter(Boolean) : []);
                    this.urlHydrated = true;
                }

                if (params.get('action') === 'add') {
                    this.openCreate();
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: { action: null },
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                }

                const editId = params.get('edit');
                if (editId) this.pendingEditId.set(editId);
            });
    }

    protected refresh(): void {
        this.companyService.loadCompanies();
        this.realmService.loadRealms();
    }

    protected openCreate(): void {
        this.editingCompany.set(null);
        this.editDialogVisible.set(true);
    }

    protected openEdit(company: Company): void {
        this.editingCompany.set(company);
        this.editDialogVisible.set(true);
    }

    protected openDelete(company: Company): void {
        this.deleteTarget.set(company);
        this.deleteDialogVisible.set(true);
    }

    protected onCompanyCreated(company: Company): void {
        if (this.highlightTimer) clearTimeout(this.highlightTimer);
        this.highlightedId.set(company.Id);
        this.highlightTimer = setTimeout(() => {
            this.highlightedId.set(null);
            this.highlightTimer = null;
        }, HIGHLIGHT_DURATION_MS);
    }

    protected viewWebsites(company: Company): void {
        this.router.navigate(['/website'], { queryParams: { company: company.Id } });
    }

    protected websiteCount(companyId: string): number {
        return this.websiteCountByCompany().get(companyId) ?? 0;
    }

    protected clearFilters(): void {
        this.searchTerm.set('');
        this.selectedCountries.set([]);
    }

    protected exportCsv(): void {
        const rows = this.filteredCompanies();
        const header = ['Created at', 'Name', 'Country code', 'Country', 'Address', 'Registration details', 'Stripe', 'LiveChat Group ID'];
        const escape = (v: string): string => {
            if (v == null) return '';
            const needsQuote = /[",\r\n]/.test(v);
            const safe = v.replace(/"/g, '""');
            return needsQuote ? `"${safe}"` : safe;
        };
        const lines = [
            header.map(escape).join(','),
            ...rows.map(c => [
                c.CreatedAt,
                c.Name,
                c.CountryCode,
                this.countryService.getName(c.CountryCode),
                c.Address,
                c.RegistrationDetails,
                c.Stripe.IsEnabled ? 'Yes' : 'No',
                c.LiveChatConfig.GroupId ?? '',
            ].map(escape).join(',')),
        ];
        const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const ts = new Date().toISOString().slice(0, 10);
        const link = document.createElement('a');
        link.href = url;
        link.download = `companies-${ts}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    protected trackById(_index: number, item: Company): string {
        return item.Id;
    }
}
